import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { publicImageUrl, cardImageUrl, cardName } from "@/lib/api";
import { X, Trash2, ShoppingCart, ArrowRight, MapPin, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { humanUploadError } from "@/lib/imageCompress";
import { isSealed, sealedLabel, sealedPillClass } from "@/lib/productType";

const LANG = { es: "ES", en: "EN", ja: "JP", otro: "Otro" };

export function CartDrawer() {
  const { cart, drawerOpen, closeDrawer, remove, setQuantity } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [pending, setPending] = useState({});  // pubId → boolean (bloquea +/- mientras hay request en curso)

  const changeQty = async (it, delta) => {
    const pubId = it.publication_id;
    const stock = it.publication?.quantity ?? 999;
    const target = it.quantity + delta;
    if (target > stock) {
      toast.warning(`Solo hay ${stock} unidad(es) en stock`);
      return;
    }
    if (target <= 0) {
      // Baja a 0 → eliminar (con undo mediante backup)
      const backup = { pubId, quantity: it.quantity };
      setPending((p) => ({ ...p, [pubId]: true }));
      try {
        await remove(pubId);
        toast.success("Ítem eliminado", {
          duration: 4000,
          action: {
            label: "Deshacer",
            onClick: async () => {
              try {
                await setQuantity(backup.pubId, backup.quantity);
              } catch (err) {
                toast.error(humanUploadError(err));
              }
            },
          },
        });
      } catch (err) {
        toast.error(humanUploadError(err));
      } finally {
        setPending((p) => ({ ...p, [pubId]: false }));
      }
      return;
    }
    setPending((p) => ({ ...p, [pubId]: true }));
    try {
      await setQuantity(pubId, target);
    } catch (err) {
      toast.error(humanUploadError(err));
    } finally {
      setPending((p) => ({ ...p, [pubId]: false }));
    }
  };

  // Cerrar con Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === "Escape") closeDrawer(); };
    window.addEventListener("keydown", onKey);
    // Bloquear scroll del body cuando el drawer está abierto
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, closeDrawer]);

  const goCheckout = () => {
    if (!user) { closeDrawer(); nav("/login"); return; }
    if (user.verification_status !== "aprobado") {
      closeDrawer();
      toast.error("Debes verificar tu identidad primero");
      nav("/verify");
      return;
    }
    closeDrawer();
    nav("/cart");  // /cart tiene el flujo completo con selector de envío/encuentro
  };

  const resolveImg = (it) => {
    const cardImg = it.card?.image_url;
    const pubImg = it.publication?.photos?.[0];
    // Feb 2026 v30.7 — Yu-Gi-Oh devuelve /api/img/yugioh/{id} ya absoluto al
    // backend; cardImageUrl() lo detecta sin prefijar /api/files/public/.
    if (cardImg) return cardImageUrl(cardImg);
    if (pubImg) return publicImageUrl(pubImg);
    return null;
  };

  if (!drawerOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={closeDrawer}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
        data-testid="cart-drawer-overlay"
      />
      {/* Panel lateral (drawer) */}
      <aside
        className="fixed top-0 right-0 z-50 h-full w-[85vw] sm:w-[420px] bg-[#0A0A0A] border-l border-[#262626] shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
        role="dialog"
        aria-modal="true"
        aria-label="Carrito"
        data-testid="cart-drawer"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#262626]">
          <div className="flex items-center gap-2">
            <ShoppingCart size={18} className="text-[#EF4444]" />
            <h2 className="font-display text-xl">Carrito ({cart.count})</h2>
          </div>
          <button
            onClick={closeDrawer}
            className="text-neutral-400 hover:text-white p-1 rounded transition-colors"
            data-testid="cart-drawer-close"
            aria-label="Cerrar carrito"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {cart.groups.length === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart size={40} className="text-neutral-700 mx-auto mb-3" />
              <div className="text-neutral-400 mb-4">Tu carrito está vacío.</div>
              <Button
                onClick={() => { closeDrawer(); nav("/catalog"); }}
                className="bg-[#EF4444] hover:bg-[#DC2626]"
                data-testid="cart-drawer-catalog"
              >
                Explorar catálogo
              </Button>
            </div>
          ) : (
            <div className="p-4 space-y-4">
              {cart.groups.map((g) => {
                const neededItems = g.free_ship_min_items;
                const neededAmount = g.free_ship_min_amount;
                const have = g.item_count;
                const spent = g.subtotal;
                const unlocked = g.ship_cost === 0 && (neededItems || neededAmount);
                const unlockedBy = g.free_ship_unlocked_by;
                // Compute progress on the mode that's closest to unlock
                const itemsPct = neededItems ? Math.min(100, Math.round((have / neededItems) * 100)) : 0;
                const amountPct = neededAmount ? Math.min(100, Math.round((spent / neededAmount) * 100)) : 0;
                return (
                <div key={g.seller.id} className="bg-[#111] border border-[#262626] rounded-md overflow-hidden">
                  <div className="px-3 py-2 border-b border-[#262626] flex items-center justify-between text-xs">
                    <div className="min-w-0">
                      <div className="text-neutral-300 truncate">Vendedor: <span className="text-white">{g.seller.name}</span></div>
                      {g.seller.province && (
                        <div className="text-neutral-500 flex items-center gap-1"><MapPin size={10} /> {g.seller.province}</div>
                      )}
                    </div>
                    <div className="text-neutral-500 shrink-0 ml-2">
                      {g.ship_cost === 0 ? "Envío gratis" : `Envío $${g.ship_cost.toFixed(2)}`}
                    </div>
                  </div>
                  {(neededItems || neededAmount) && (
                    <div
                      className={`px-3 py-2 border-b border-[#262626] text-xs ${unlocked ? "bg-emerald-500/10 text-emerald-200" : "bg-amber-500/10 text-amber-200"}`}
                      data-testid={`free-ship-progress-${g.seller.id}`}
                    >
                      {unlocked ? (
                        <div className="flex items-center gap-2 font-medium">
                          🎉 ¡Envío gratis desbloqueado con <span className="text-white">{g.seller.name}</span>! ({unlockedBy === "items" ? `${have} artículos` : `$${spent.toFixed(2)} de compra`})
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {neededItems && (
                            <div>
                              <div className="mb-1">Por cantidad: <b className="text-white">{have} de {neededItems}</b> artículos {have < neededItems && <>· agrega <b>{neededItems - have}</b> más</>}</div>
                              <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-400 transition-all" style={{ width: `${itemsPct}%` }} />
                              </div>
                            </div>
                          )}
                          {neededAmount && (
                            <div>
                              <div className="mb-1">Por monto: <b className="text-white">${spent.toFixed(2)} de ${neededAmount.toFixed(2)}</b> {spent < neededAmount && <>· suma <b>${(neededAmount - spent).toFixed(2)}</b> más</>}</div>
                              <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                                <div className="h-full bg-amber-400 transition-all" style={{ width: `${amountPct}%` }} />
                              </div>
                            </div>
                          )}
                          <div className="text-[10px] text-amber-300/80">
                            {neededItems && neededAmount ? "Cumple CUALQUIERA de los dos y el envío es gratis." : "Ahorrarás ${" + g.ship_cost.toFixed(2) + "} de envío."}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="divide-y divide-[#171717]">
                    {g.items.map((it) => {
                      const img = resolveImg(it);
                      const stock = it.publication?.quantity ?? 0;
                      const atMax = it.quantity >= stock;
                      const busy = !!pending[it.publication_id];
                      const sealed = isSealed(it);
                      return (
                        <div key={it.id} className={`flex items-center gap-3 p-3 border-l-2 ${sealed ? "border-l-purple-500" : "border-l-transparent"}`} data-testid={`drawer-item-${it.publication_id}`}>
                          <div className={`w-12 h-16 bg-[#0A0A0A] rounded overflow-hidden shrink-0 flex items-center justify-center border ${sealed ? "border-purple-500/60" : "border-transparent"}`}>
                            {img ? (
                              <img src={img} alt="" className="w-full h-full object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                            ) : (
                              <span className="text-[9px] text-neutral-600">Sin foto</span>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <div className="text-sm line-clamp-1 flex-1 min-w-0">{cardName(it.card, it.publication?.language)}</div>
                              {it.publication?.first_edition && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/60 text-amber-200" data-testid={`drawer-first-edition-${it.publication_id}`}>1.ª ED.</span>
                              )}
                              {sealed && (
                                <span className={sealedPillClass} data-testid={`drawer-sealed-${it.publication_id}`}>
                                  📦 {sealedLabel(it)}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-neutral-500">{LANG[it.publication.language]} · {it.publication.condition}</div>
                            <div className="mt-1.5 flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => changeQty(it, -1)}
                                disabled={busy}
                                className="w-6 h-6 rounded border border-[#262626] hover:border-[#EF4444] hover:text-[#EF4444] flex items-center justify-center disabled:opacity-40"
                                data-testid={`drawer-qty-minus-${it.publication_id}`}
                                aria-label="Bajar cantidad"
                              >
                                <Minus size={12} />
                              </button>
                              <span className="text-sm w-8 text-center font-medium" data-testid={`drawer-qty-value-${it.publication_id}`}>{it.quantity}</span>
                              <button
                                type="button"
                                onClick={() => changeQty(it, +1)}
                                disabled={busy || atMax}
                                className="w-6 h-6 rounded border border-[#262626] hover:border-[#EF4444] hover:text-[#EF4444] flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                                data-testid={`drawer-qty-plus-${it.publication_id}`}
                                aria-label="Subir cantidad"
                                title={atMax ? `Máximo alcanzado (${stock})` : "Sumar 1"}
                              >
                                <Plus size={12} />
                              </button>
                              <span className={`text-[10px] ml-1 ${atMax ? "text-amber-400" : "text-neutral-600"}`}>
                                {atMax ? `Máx (${stock})` : `de ${stock}`}
                              </span>
                            </div>
                          </div>
                          <div className="text-right shrink-0 self-start">
                            <div className={`text-sm font-medium ${sealed ? "text-purple-300" : "text-[#EF4444]"}`}>${Number(it.buyer_pays_line ?? (it.publication.buyer_pays ?? it.publication.price) * it.quantity).toFixed(2)}</div>
                            <button
                              onClick={() => remove(it.publication_id)}
                              className="text-neutral-500 hover:text-[#EF4444] p-1 mt-1"
                              data-testid={`drawer-remove-${it.publication_id}`}
                              aria-label="Eliminar"
                            >
                              <Trash2 size={12} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {cart.groups.length > 0 && (
          <div className="border-t border-[#262626] p-4 space-y-3 bg-[#0A0A0A]">
            <div className="flex justify-between items-baseline">
              <span className="text-xs text-neutral-500 uppercase tracking-wider">Total estimado</span>
              <span className="font-display text-2xl text-[#EF4444]" data-testid="cart-drawer-total">
                ${cart.grand_total?.toFixed(2) || "0.00"}
              </span>
            </div>
            <Button
              onClick={goCheckout}
              className="w-full bg-[#EF4444] hover:bg-[#DC2626] h-11"
              data-testid="cart-drawer-checkout"
            >
              Ir a pagar <ArrowRight size={16} className="ml-2" />
            </Button>
            <div className="text-[10px] text-neutral-600 text-center">
              El envío final y encuentro presencial se eligen en el siguiente paso.
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
